import WidgetItem from '@components/PlayerWidgets/WidgetItem';
import Tip from '@components/Tip';
import { offset, useFloating } from '@floating-ui/react-dom';
import { MediaType, type STImageMarker } from '@types';
import { getUrlByMediaType } from '@utils/urlMakers';
import { useEffect } from 'react';
import { MARKER_IMAGE_SIZE, MARKER_SIZE } from '../constants';

interface Props {
  data: STImageMarker;
  reversed?: boolean; // krp 位置相关是反的
  isActive: boolean;
}

export function BatchPreviewWidget({ data, reversed, isActive }: Props) {
  const { icon, showTitle, titleSetting } = data;
  const {
    type = 0,
    scaleX,
    scaleY,
    fillColor,
    borderColor,
    activeFillColor,
    activeBorderColor,
    mediaIcon,
  } = icon;
  const { oy, ox, background, edge, align, font, writingMode } = titleSetting;

  const getPlacement = () => {
    if (!reversed) return edge;
    if (edge === 'top') return 'bottom';
    if (edge === 'bottom') return 'top';
    if (edge === 'left') return 'right';
    if (edge === 'right') return 'left';
    return 'bottom';
  };

  const getOffset = () => {
    if (!reversed) {
      if (edge === 'top') return { mainAxis: -oy, crossAxis: ox };
      if (edge === 'bottom') return { mainAxis: oy, crossAxis: ox };
      if (edge === 'left') return { mainAxis: -ox, crossAxis: oy };
      if (edge === 'right') return { mainAxis: ox, crossAxis: oy };
      return { mainAxis: 0, crossAxis: 0 };
    }
    if (edge === 'top') return { mainAxis: oy, crossAxis: ox };
    if (edge === 'bottom') return { mainAxis: -oy, crossAxis: ox };
    if (edge === 'left') return { mainAxis: ox, crossAxis: oy };
    if (edge === 'right') return { mainAxis: -ox, crossAxis: oy };
    return { mainAxis: 0, crossAxis: 0 };
  };

  const { x, y, reference, floating, strategy, update } = useFloating({
    placement: getPlacement(),
    middleware: [offset(getOffset())],
  });

  useEffect(() => {
    update();
  }, [data, update]);

  const renderMarker = () => {
    if (type === 1 && mediaIcon) {
      const width = MARKER_IMAGE_SIZE * scaleX;
      const height = MARKER_IMAGE_SIZE * scaleY;
      const url = getUrlByMediaType(
        mediaIcon.type ? MediaType.lib : MediaType.system,
        mediaIcon.url
      );
      return (
        <div
          ref={reference}
          className="flex items-center justify-center relative"
        >
          <img src={url} style={{ width, height }} draggable={false} alt="" />
        </div>
      );
    }
    return (
      <div
        ref={reference}
        style={{
          width: MARKER_SIZE * scaleX,
          height: MARKER_SIZE * scaleY,
          backgroundColor: isActive
            ? `rgba(${activeFillColor})`
            : `rgba(${fillColor})`,
          border: isActive
            ? `2px solid rgba(${activeBorderColor})`
            : `2px solid rgba(${borderColor})`,
          borderRadius: '999px',
        }}
      />
    );
  };

  return (
    <WidgetItem title="批量添加样式预览" width={250}>
      <div className="relative flex h-150 items-center justify-center overflow-hidden rounded bg-black-#28">
        {renderMarker()}
        {!!showTitle && (
          <div
            ref={floating}
            style={{
              position: strategy,
              top: y ?? '',
              left: x ?? '',
              fontSize: font.fontSize,
              color: `rgba(${font.fontColor})`,
              textAlign: align,
              backgroundColor: `rgba(${background.bgColor})`,
              padding: '5px 10px',
              borderRadius: '5px',
              lineHeight: `${font.fontSize * 1.4}px`,
              whiteSpace: 'pre',
            }}
          >
            场景名称
          </div>
        )}
      </div>
      <Tip className="mt-10 text-orange">
        注：标题文字和雷达方向在批量添加后可以单独对每个标记进行调整。
      </Tip>
    </WidgetItem>
  );
}
